import { lazy, Suspense, useContext, useEffect, useState } from 'react'
import { CampaignContext, ProjectContext, TemplateContext } from '../../../contexts'
import './EmailEditor.css'
import Button, { LinkButton } from '../../../ui/Button'
import api from '../../../api'
import { Resource, Template } from '../../../types'
import { useBlocker, useNavigate } from 'react-router'
import Modal from '../../../ui/Modal'
import HtmlEditor from './HtmlEditor'
import PopupEditor from './PopupEditor'
import LocaleSelector from '../locale/LocaleSelector'
import { toast } from 'react-hot-toast/headless'
import { CloseIcon, DownloadIcon, QuestionIcon, SendIcon } from '../../../ui/icons'
import { useTranslation } from 'react-i18next'
import ResourceModal from './ResourceModal'
import { TemplateContextProvider } from '../template/TemplateContextProvider'
import VariantSelector from '../variants/VariantSelector'
import BeefreeEditor from './BeefreeEditor'
import CampaignSendProof from '../preview/CampaignSendProof'
import { ButtonGroup } from '../../../ui'

export interface Editor {
    save: () => Promise<Template>
}

const VisualEditor = lazy(async () => await import('./VisualEditor'))

function EmailEditor() {

    const navigate = useNavigate()
    const { t } = useTranslation()
    const [project] = useContext(ProjectContext)
    const { campaign, setCampaign, currentTemplate, setTemplate } = useContext(TemplateContext)
    const { templates } = campaign

    const [resources, setResources] = useState<Resource[]>([])

    const [isSaving, setIsSaving] = useState(false)
    const [showConfig, setShowConfig] = useState(false)
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
    const [editor, setEditor] = useState<Editor | undefined>()
    const [isSendProofOpen, setIsSendProofOpen] = useState(false)

    useEffect(() => {
        api.resources.all(project.id, 'font')
            .then(resources => setResources(resources))
            .catch(() => setResources([]))
    }, [])

    const blocker = useBlocker(
        ({ currentLocation, nextLocation }) => hasUnsavedChanges && currentLocation.pathname !== nextLocation.pathname,
    )

    useEffect(() => {
        if (blocker.state !== 'blocked') return
        if (confirm(t('confirm_unsaved_changes'))) {
            blocker.proceed()
        } else {
            blocker.reset()
        }
    }, [blocker.state])

    async function handleTemplateSave({ id, data }: Template) {
        setIsSaving(true)
        if (editor) {
            const savedTemplate = await editor?.save()
            data = savedTemplate.data
            id = savedTemplate.id
        }

        try {
            const value = await api.templates.update(project.id, id, { data })

            const newCampaign = { ...campaign }
            newCampaign.templates = templates.map(obj => obj.id === id ? value : obj)
            setCampaign(newCampaign)
            setHasUnsavedChanges(false)
            toast.success(t('template_saved'))
        } catch (error: any) {
            const data = error?.response?.data?.error
            const message = (typeof data === 'string' && data)
                || data?.message
                || error?.message
                || t('unknown_error')
            toast.error(t('unable_to_save', { error: message }))
        } finally {
            setIsSaving(false)
        }
    }

    const handleTemplateChange = (template: Template | undefined) => {
        setHasUnsavedChanges(true)
        setTemplate(template)
    }

    const handleBeforeSendProof = async () => {
        if (currentTemplate && hasUnsavedChanges) {
            await handleTemplateSave(currentTemplate)
        }
    }

    const handleDownloadHtml = async () => {
        const template = editor ? await editor?.save() : currentTemplate
        if (!template) return
        // Create a Blob from the HTML string
        const blob = new Blob([template.data.html], { type: 'text/html' })

        // Create a download link
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `${campaign.name}_${template.name}_${Date.now()}.html`

        // Trigger download
        document.body.appendChild(link)
        link.click()

        // Cleanup
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
    }

    const handleClose = async () => {
        const query = currentTemplate ? `?template=${currentTemplate.id}` : ''
        await navigate(`../campaigns/${campaign.id}/design${query}`)
    }

    const renderEditor = (currentTemplate: Template) => {
        if (currentTemplate?.data.editor === 'bee') {
            return (
                <BeefreeEditor
                    template={currentTemplate}
                    setTemplate={handleTemplateChange}
                    setEditor={setEditor}
                />
            )
        }
        if (currentTemplate?.data.editor === 'visual') {
            return (
                <Suspense key={currentTemplate.id} fallback={null}>
                    <VisualEditor
                        template={currentTemplate}
                        setTemplate={handleTemplateChange}
                        resources={resources}
                    />
                </Suspense>
            )
        }
        if (currentTemplate?.data.editor === 'popup') {
            return (
                <PopupEditor
                    key={currentTemplate.id}
                    template={currentTemplate}
                    setTemplate={handleTemplateChange}
                />
            )
        }
        return (
            <HtmlEditor
                template={currentTemplate}
                key={currentTemplate.id}
                setTemplate={handleTemplateChange}
            />
        )
    }

    const isPopupEditor = currentTemplate?.data.editor === 'popup'

    const headerActions = (
        <>
            {!isPopupEditor && <ButtonGroup>
                <Button
                    icon={<DownloadIcon />}
                    variant="secondary"
                    size="small"
                    title={t('editor_action_download')}
                    onClick={async () => await handleDownloadHtml()}
                />
                {currentTemplate?.data.editor !== 'bee' && <Button
                    size="small"
                    variant="secondary"
                    onClick={() => setShowConfig(true)}
                >{t('config')}</Button>}
                <LinkButton
                    icon={<QuestionIcon />}
                    variant="secondary"
                    size="small"
                    title={t('editor_action_help')}
                    to="https://docs.parcelvoy.com/how-to/campaigns/templates"
                    target="_blank" />
            </ButtonGroup>}
            <VariantSelector />
            <LocaleSelector />
            {currentTemplate && <>
                <Button
                    size="small"
                    variant="secondary"
                    icon={<SendIcon />}
                    onClick={() => setIsSendProofOpen(true)}
                >{t('send_proof')}</Button>
                <Button
                    size="small"
                    isLoading={isSaving}
                    onClick={async () => await handleTemplateSave(currentTemplate)}
                >{t('template_save')}</Button>
            </>}
        </>
    )

    if (!currentTemplate && templates.length) return null

    // Popup editor: use a plain fullscreen div instead of HeadlessUI Dialog
    // HeadlessUI Dialog uses portals + inert attributes that break dnd-kit
    if (isPopupEditor) {
        return (
            <div className="popup-editor-fullscreen">
                <div className="popup-editor-header">
                    <Button
                        variant="secondary"
                        onClick={handleClose}
                        icon={<CloseIcon />}
                    >
                        {t('exit')}
                    </Button>
                    <h3 className="popup-editor-title">{campaign.name}</h3>
                    <div className="popup-editor-actions">
                        {headerActions}
                    </div>
                </div>
                {currentTemplate && <section className="popup-editor-content">
                    {renderEditor(currentTemplate)}
                </section>}
                {currentTemplate && <CampaignSendProof
                    open={isSendProofOpen}
                    onClose={setIsSendProofOpen}
                    templateId={currentTemplate.id}
                    type={currentTemplate.type}
                    onBeforeSend={handleBeforeSendProof}
                />}
            </div>
        )
    }

    return (
        <Modal
            size="fullscreen"
            title={campaign.name}
            open
            onClose={handleClose}
            actions={headerActions}
        >
            {currentTemplate && <section className="email-editor">
                {renderEditor(currentTemplate)}
            </section>}

            <ResourceModal
                open={showConfig}
                onClose={() => setShowConfig(false)}
                resources={resources}
                setResources={setResources}
            />
            {currentTemplate && <CampaignSendProof
                open={isSendProofOpen}
                onClose={setIsSendProofOpen}
                templateId={currentTemplate.id}
                type={currentTemplate.type}
                onBeforeSend={handleBeforeSendProof}
            />}
        </Modal>
    )
}

export default function EmailEditorWrapper() {
    const [campaign, setCampaign] = useContext(CampaignContext)
    return (
        <>
            <TemplateContextProvider campaign={campaign} setCampaign={setCampaign}>
                <EmailEditor />
            </TemplateContextProvider>
        </>
    )
}
